/**
 * Inking the map sheet: everything on the paper that is a line or a dot rather
 * than a glyph. Pure — a `MapSheet` in, a palette-index raster out — so a
 * headless test can assert that the front line, the network and the pilot's
 * own mark reached the paper by counting pixels of their own ink.
 *
 * Draw order is a printer's: the water wash goes down first because it is the
 * one thing on the sheet that is an *area* rather than a mark, then the grid
 * (printed on the blank sheet before the geography), then the terrain, then the
 * front, then whatever was pencilled on before take-off, and the aeroplane's
 * own mark last, on top of all of it.
 */

import { digitGridLines, letterGridLines } from "./sector-code.ts";
import { mapProjection, MAP_PAPER, FEATURE_INK, FRONT_INK, GRID_INK, PAPER, PENCIL_INK, PLAYER_INK, ROUTE_INK, SCENERY_INK, SEA_INK, TARGET_INK, WATER_INK } from "./map-layout.ts";
import type { MapMarker, MapModel, MapPlayer, MapPoint } from "./map-model.ts";
import { wldPointOf } from "./map-model.ts";
import type { MapTerrain } from "./map-network.ts";
import { circleOutline, createRaster, dashedLine, fillPolygon, fillRect, line, plot, type IndexRaster } from "./map-raster.ts";
import { WLD_UNITS_PER_METRE } from "../world/wld-units.ts";
import type { MapProjection } from "../assets/world-map-projection.ts";

/** Everything the sheet draws, and the one decision REAL NAVIGATION makes about it. */
export interface MapSheet {
  readonly model: MapModel;
  readonly terrain: MapTerrain;
  /** Where the aeroplane is. `undefined` before the first tick of a flight. */
  readonly player: MapPlayer | undefined;
  /**
   * REAL NAVIGATION, as the original means it: with the toggle **on** the game
   * withholds where you are. The sheet still opens — a pilot carries his map
   * either way — and everything printed on it stays; what goes is the pilot's
   * own position mark. See `map-screen.ts` for the readout half of the same
   * decision, and this lane's report for the flag-sense evidence.
   */
  readonly hidePosition: boolean;
  /**
   * The blink phase of the pilot's own mark. The overlay toggles this on a
   * timer while the sheet is open, so the mark pulses in and out of view — a
   * static mark is easy to lose on a busy sheet, and the one thing a glance at
   * the map is looking for should be the one thing that moves.
   * Defaults to shown; `REAL NAVIGATION` (`hidePosition`) still takes it off
   * the sheet outright.
   */
  readonly blinkOn?: boolean;
}

const AERODROME_RADIUS_PX = 3;
const WAYPOINT_RADIUS_PX = 2;
const TARGET_RADIUS_PX = 3;

type Screen = { readonly x: number; readonly y: number };

function project(projection: MapProjection, point: MapPoint): Screen {
  return projection.toScreen({ x: point.u, y: point.v });
}

/**
 * The printed sector grid. Positions come straight from the original's own
 * constants (`sector-code.ts`), converted from `.WLD` units into map space by
 * undoing the region's recentring — so a grid line on this sheet is a grid
 * line the 1990 executable would have counted the same way.
 */
function inkGrid(raster: IndexRaster, model: MapModel, projection: MapProjection): void {
  const corner = (u: number, v: number) => wldPointOf(model, { u, v });
  const min = corner(model.bounds.minU, model.bounds.minV);
  const max = corner(model.bounds.maxU, model.bounds.maxV);
  for (const wldX of digitGridLines(min.x, max.x)) {
    const u = wldX / WLD_UNITS_PER_METRE - model.origin.x;
    const top = project(projection, { u, v: model.bounds.maxV });
    const bottom = project(projection, { u, v: model.bounds.minV });
    dashedLine(raster, top.x, top.y, bottom.x, bottom.y, GRID_INK, 1, 2);
  }
  for (const wldY of letterGridLines(min.y, max.y)) {
    const v = wldY / WLD_UNITS_PER_METRE - model.origin.z;
    const left = project(projection, { u: model.bounds.minU, v });
    const right = project(projection, { u: model.bounds.maxU, v });
    dashedLine(raster, left.x, left.y, right.x, right.y, GRID_INK, 1, 2);
  }
}

/**
 * A dashed ring around a dot. Dashed because an aerodrome's position on this
 * map is *inferred* — world-terrain.md §2.4 never resolved `AERO.DAT`'s map
 * pixels to `.WLD` world units, so `world/aerodrome-projection.ts` fits one —
 * and a broken outline is the cartographer's own way of saying "about here".
 * The same idiom, for the same reason, as `/assets`' World pane's dashed
 * diamond.
 */
function inkAerodrome(raster: IndexRaster, at: Screen): void {
  const steps = 16;
  for (let step = 0; step < steps; step += 1) {
    if (step % 2 === 1) continue;
    const angle = (step / steps) * Math.PI * 2;
    plot(raster, at.x + Math.cos(angle) * AERODROME_RADIUS_PX, at.y + Math.sin(angle) * AERODROME_RADIUS_PX, PENCIL_INK);
  }
  fillRect(raster, at.x - 1, at.y - 1, 3, 3, PENCIL_INK);
}

/** A ring with a cross through it — a target, the thing a briefing tells you to go and break. */
function inkTarget(raster: IndexRaster, at: Screen): void {
  circleOutline(raster, at.x, at.y, TARGET_RADIUS_PX, TARGET_INK);
  line(raster, at.x - TARGET_RADIUS_PX, at.y, at.x + TARGET_RADIUS_PX, at.y, TARGET_INK);
  line(raster, at.x, at.y - TARGET_RADIUS_PX, at.x, at.y + TARGET_RADIUS_PX, TARGET_INK);
}

/** The briefed route: dashed legs between the waypoints, a small ring at each. */
function inkRoute(raster: IndexRaster, waypoints: readonly MapMarker[], projection: MapProjection): void {
  let previous: Screen | undefined;
  for (const waypoint of waypoints) {
    const at = project(projection, waypoint);
    if (previous) dashedLine(raster, previous.x, previous.y, at.x, at.y, ROUTE_INK, 2, 3);
    circleOutline(raster, at.x, at.y, WAYPOINT_RADIUS_PX, ROUTE_INK);
    previous = at;
  }
}

/**
 * The pilot's own mark: a solid 4px orange dot on the fix (user, 2026-08-23).
 * The first draft was a stubby arrow with a 3px core — it carried the heading
 * and was easy to lose against the network; a solid 4px block is the most
 * glance-findable mark the sheet can carry, and the overlay blinks it in and
 * out so a glance finds it.
 */
function inkPlayer(raster: IndexRaster, player: MapPlayer, projection: MapProjection): void {
  const at = project(projection, player);
  fillRect(raster, at.x - 2, at.y - 2, 4, 4, PLAYER_INK);
}

/**
 * The sea, first of everything. It is a wash of area rather than a mark, so it
 * goes down before the grid the way a printer lays a flat colour before the
 * plate that carries the detail — the grid, the towns, the route and the
 * aeroplane all read over the top of it, which is why the wash is pale.
 *
 * The coastline is stroked afterwards in the water's own line ink: the fill's
 * own edge is a stair-stepped scanline boundary, and a drawn edge is what makes
 * a shore look like a shore. `map-water.ts` supplies only the *outer* edges of
 * the water — the interior diagonals of the plates' triangulation cancel — so
 * this is a coast, not a wire-frame of the North Sea.
 */
function inkWater(raster: IndexRaster, terrain: MapTerrain, projection: MapProjection): void {
  for (const face of terrain.water.faces) {
    fillPolygon(raster, face.points.map((point) => project(projection, point)), SEA_INK);
  }
  for (const edge of terrain.water.coastline) {
    const a = project(projection, edge.a);
    const b = project(projection, edge.b);
    line(raster, a.x, a.y, b.x, b.y, WATER_INK);
  }
}

/** Rasterizes the whole sheet at `MAP_PAPER`'s size, in paper-local pixels. */
export function paintMapPaper(sheet: MapSheet): IndexRaster {
  const { model } = sheet;
  const raster = createRaster(MAP_PAPER.w, MAP_PAPER.h, PAPER);
  const projection = mapProjection(model.bounds);

  inkWater(raster, sheet.terrain, projection);
  inkGrid(raster, model, projection);

  for (const point of model.scenery) {
    const at = project(projection, point);
    plot(raster, at.x, at.y, SCENERY_INK);
  }
  // Faintest class first so the roads and rivers print over the field
  // boundaries rather than under them — the same reason a printed map runs its
  // plates light to dark.
  for (const feature of ["field", "wood", "other", "water", "road"] as const) {
    for (const segment of sheet.terrain.segments) {
      if (segment.feature !== feature) continue;
      const a = project(projection, segment.a);
      const b = project(projection, segment.b);
      line(raster, a.x, a.y, b.x, b.y, FEATURE_INK[feature]);
    }
  }
  for (const hill of model.hills) {
    const at = project(projection, hill);
    circleOutline(raster, at.x, at.y, Math.max(2, hill.radiusM * projection.scale), PENCIL_INK);
  }
  if (model.frontLineV !== undefined) {
    const left = project(projection, { u: model.bounds.minU, v: model.frontLineV });
    const right = project(projection, { u: model.bounds.maxU, v: model.frontLineV });
    dashedLine(raster, left.x, left.y, right.x, right.y, FRONT_INK, 4, 2);
    dashedLine(raster, left.x, left.y + 1, right.x, right.y + 1, FRONT_INK, 4, 2);
  }

  inkRoute(raster, model.waypoints, projection);
  for (const target of model.targets) inkTarget(raster, project(projection, target));
  for (const aerodrome of model.aerodromes) inkAerodrome(raster, project(projection, aerodrome));
  if (model.home) inkAerodrome(raster, project(projection, model.home));
  if (sheet.player && !sheet.hidePosition && sheet.blinkOn !== false) inkPlayer(raster, sheet.player, projection);

  return raster;
}
